import { useCallback } from "react";
import { createFileRoute, useNavigate, type SearchSchemaInput } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button, PageHeader, Tabs, type TabDef } from "@/components/ui";
import { DashboardsListPanel } from "@/components/reporting/dashboards/dashboards-list-panel";
import { ReportsListPanel } from "@/components/reporting/reports-list-panel";
import { usePermissions } from "@/hooks/use-permissions";
import { useAppStore } from "@/store";

type ReportsTab = "reports" | "dashboards";

const ALLOWED_TABS: ReportsTab[] = ["reports", "dashboards"];

interface ReportsSearch {
  tab: ReportsTab;
}

/**
 * `SearchSchemaInput` keeps the INPUT half optional while the output stays a
 * resolved tab: without it TanStack infers the input from the output and every
 * `to: "/reports"` — the sidebar's `Link` included — would have to spell a tab
 * it has no opinion about.
 */
type ReportsSearchInput = { tab?: string } & SearchSchemaInput;

function parseTab(value: string | undefined): ReportsTab {
  return ALLOWED_TABS.find((tab) => tab === value) ?? "reports";
}

export const Route = createFileRoute("/_authenticated/reports/")({
  component: ReportsIndexRoute,
  validateSearch: (search: ReportsSearchInput): ReportsSearch => ({ tab: parseTab(search.tab) }),
});

/**
 * The reporting home: one tabbed surface over the org's saved reports and the
 * dashboards that arrange them. The route owns the page gutter, the header and
 * the tab strip — all three persist across both tabs — while only the ACTIVE
 * panel mounts, so the tab that is not on fires none of its queries.
 *
 * The header's action belongs to the tab under it: each panel's own empty-state
 * invitation and the header button above it are the SAME navigation, gated on
 * the same `reports:create` grant, so a reader is never offered one and refused
 * the other.
 *
 * The tab rides `?tab=` against the allow-list above (absent or unknown reads as
 * Reports) and a tab switch replaces, so history keeps one entry per visit
 * rather than one per tab.
 */
function ReportsIndexRoute() {
  const { t } = useTranslation(["reporting", "org"]);
  const navigate = useNavigate();
  const { tab } = Route.useSearch();
  const orgId = useAppStore((s) => s.activeOrgId);
  const { can } = usePermissions();
  // Memoized where its siblings are not, and declared above the no-org return
  // because it is a hook: it reaches the dashboards panel's row actions through
  // a `useMemo`, and a fresh function each render would rebuild every column
  // def under the table.
  const goHome = useCallback(() => {
    void navigate({ to: "/" });
  }, [navigate]);

  if (!orgId) {
    return (
      <div className="p-8">
        <p className="text-muted">{t("noOrgContext", { ns: "org" })}</p>
      </div>
    );
  }

  const tabs: TabDef<ReportsTab>[] = [
    { value: "reports", label: t("list.tabs.reports") },
    { value: "dashboards", label: t("list.tabs.dashboards") },
  ];

  const openReport = (reportId: string) => {
    void navigate({ to: "/reports/$id", params: { id: reportId } });
  };
  const newReport = () => {
    void navigate({ to: "/reports/new" });
  };
  const newDashboard = () => {
    void navigate({ to: "/reports/dashboards/new" });
  };
  const openDashboard = (dashboardId: string) => {
    void navigate({ to: "/reports/dashboards/$id", params: { id: dashboardId } });
  };

  const onDashboards = tab === "dashboards";

  return (
    <div className="space-y-6 p-8">
      <PageHeader
        title={t("list.title")}
        subtitle={t("list.subtitle")}
        actions={
          can("reports", "create") ? (
            <Button onClick={onDashboards ? newDashboard : newReport}>
              {onDashboards ? t("list.dashboards.newDashboard") : t("list.newReport")}
            </Button>
          ) : undefined
        }
      />

      <Tabs
        tabs={tabs}
        activeTab={tab}
        onTabChange={(next) => {
          void navigate({ to: "/reports", search: { tab: next }, replace: true });
        }}
        ariaLabel={t("list.title")}
      />

      <div role="tabpanel" id={`tabpanel-${tab}`} aria-labelledby={`tab-${tab}`}>
        {onDashboards ? (
          <DashboardsListPanel
            orgId={orgId}
            onOpenDashboard={openDashboard}
            onNewDashboard={newDashboard}
            onGoHome={goHome}
          />
        ) : (
          <ReportsListPanel orgId={orgId} onOpenReport={openReport} onNewReport={newReport} />
        )}
      </div>
    </div>
  );
}
